import type { CaseMatterType } from './caseTaxonomyRegistry';

/** Frozen definitions for removed matter types — used only for existing cases on disk. */
export const WRIT_LEGACY_MATTER_TYPE_IDS = [
  'Service Matter',
  'Habeas Corpus',
  'Constitutional Petition',
  'Judicial Review',
  'Public Law / Fundamental Rights',
  'Constitutional Miscellaneous Application',
] as const;

export type WritLegacyMatterTypeId = (typeof WRIT_LEGACY_MATTER_TYPE_IDS)[number];

export const WRIT_LEGACY_MATTER_TYPES: CaseMatterType[] = [
  {
    id: 'Service Matter',
    label: 'Service Matter',
    shortLabel: 'Service',
    description:
      'Writ or constitutional proceedings relating to public employment, service rights, posting, transfer, promotion, seniority, disciplinary action, retirement, pension, or departmental proceedings.',
    legalBasis: [
      'Article 199 of the Constitution for High Court writ jurisdiction, where maintainable.',
      'Service tribunal jurisdiction may bar or affect maintainability in many service matters.',
    ],
    sideOptions: ['Petitioner', 'Respondent', 'Employee', 'Department / Authority'],
    documentTabs: [
      'All',
      'Petition / Appeal',
      'Service Record',
      'Applications',
      'Departmental Orders',
      'Comments / Replies',
      'Orders & Judgments',
      'Miscellaneous',
    ],
    defaultStages: [
      'Record / Service History Review',
      'Departmental Representation / Appeal, if any',
      'Petition / Appeal Drafting',
      'Filing',
      'Office Objections / Scrutiny',
      'Preliminary Hearing / Admission',
      'Notice to Department / Authority',
      'Comments / Parawise Reply',
      'Rejoinder, if any',
      'Interim Relief / Suspension of Order, if any',
      'Record Called, if required',
      'Arguments',
      'Judgment / Final Order',
      'Compliance / Contempt / Further Remedy',
      'Closed',
    ],
    aiContextHints:
      'AI should understand employee status, department, service rules, seniority, promotion, transfer/posting, inquiry, show cause, dismissal, retirement/pension, departmental remedies, tribunal issue, and relief sought.',
  },
  {
    id: 'Habeas Corpus',
    label: 'Habeas Corpus',
    shortLabel: 'Habeas',
    description:
      'Proceedings seeking production or recovery of a person alleged to be unlawfully detained, confined, missing, or kept in illegal custody.',
    legalBasis: [
      'Article 199 habeas corpus jurisdiction before High Court.',
      'Fundamental rights / liberty context.',
    ],
    sideOptions: ['Petitioner', 'Respondent', 'Detenue', 'State / Authority'],
    documentTabs: [
      'All',
      'Habeas Petition',
      'Detenue Details',
      'Applications',
      'Police / Authority Record',
      'Orders',
      'Miscellaneous',
    ],
    defaultStages: [
      'Urgent Case Preparation',
      'Petition Drafting',
      'Filing',
      'Urgent / Preliminary Hearing',
      'Notice to Police / Authority / Respondent',
      'Production Order / Direction',
      'Report Called',
      'Production / Recovery of Detenue',
      'Statement of Detenue, if produced',
      'Final Order',
      'Compliance / Further Proceedings',
      'Closed',
    ],
    aiContextHints:
      'AI should understand detenue identity, relationship to petitioner, alleged detainer, last seen details, police involvement, urgency, illegal detention facts, recovery request, and production order.',
  },
  {
    id: 'Public Law / Fundamental Rights',
    label: 'Public Law / Fundamental Rights',
    shortLabel: 'Fundamental Rights',
    description:
      'Constitutional proceedings concerning enforcement of fundamental rights, public duties, government action, administrative fairness, and public law remedies.',
    legalBasis: [
      'Article 199 for High Court writ jurisdiction.',
      'Fundamental Rights chapter of the Constitution, depending claim.',
    ],
    sideOptions: ['Petitioner', 'Respondent', 'Public Authority', 'State'],
    documentTabs: [
      'All',
      'Petition',
      'CMAs / Applications',
      'Annexures',
      'Government / Authority Record',
      'Comments / Replies',
      'Orders & Judgments',
      'Miscellaneous',
    ],
    defaultStages: [
      'Public Law Issue / Record Review',
      'Petition Drafting',
      'Filing',
      'Office Objections / Scrutiny',
      'Preliminary Hearing / Maintainability',
      'Notice to Public Authority / State',
      'Comments / Report',
      'Rejoinder, if any',
      'Interim Relief, if any',
      'Record / Compliance Report Called',
      'Arguments',
      'Judgment / Directions',
      'Compliance / Monitoring, if any',
      'Further Remedy / Contempt, if any',
      'Closed',
    ],
    aiContextHints:
      'AI should understand fundamental right involved, public authority, public duty, public importance, affected persons, alternate remedy, administrative record, relief sought, and compliance mechanism.',
  },
  {
    id: 'Constitutional Petition',
    label: 'Constitutional Petition',
    shortLabel: 'Constitutional Petition',
    description:
      'Petitions invoking constitutional jurisdiction, including matters before High Court or Supreme Court depending on jurisdiction, subject matter, and forum.',
    legalBasis: [
      'Article 199 for High Court constitutional/writ jurisdiction.',
      'Article 184(3) / current successor constitutional jurisdiction where applicable.',
    ],
    sideOptions: ['Petitioner', 'Respondent', 'Federation / Province', 'Public Authority'],
    documentTabs: [
      'All',
      'Constitutional Petition',
      'CMAs / Applications',
      'Annexures',
      'Impugned Law / Order / Record',
      'Comments / Replies',
      'Orders & Judgments',
      'Miscellaneous',
    ],
    defaultStages: [
      'Constitutional Issue Review',
      'Petition Drafting',
      'Filing',
      'Office Objections / Scrutiny',
      'Preliminary Hearing / Maintainability',
      'Notice to Federation / Province / Respondents',
      'Comments / Report / Reply',
      'Rejoinder, if any',
      'Interim Relief / Suspension, if any',
      'Record Called / Assistance Requested',
      'Arguments',
      'Judgment / Declaration / Directions',
      'Compliance / Review / Appeal, if any',
      'Closed',
    ],
    aiContextHints:
      'AI should understand constitutional provision, challenged law/order/action, public authority, fundamental rights, public importance, maintainability, alternate remedy, and relief sought.',
  },
  {
    id: 'Judicial Review',
    label: 'Judicial Review',
    shortLabel: 'Judicial Review',
    description:
      'Proceedings challenging administrative, statutory, tribunal, departmental, or public authority decisions on grounds such as illegality, jurisdictional defect, procedural impropriety, mala fide, or violation of natural justice.',
    legalBasis: [
      'Article 199 High Court jurisdiction where maintainable.',
      'Natural justice principles.',
      'Jurisdictional error / excess of jurisdiction.',
    ],
    sideOptions: ['Petitioner', 'Respondent', 'Applicant', 'Public Authority'],
    documentTabs: [
      'All',
      'Review Petition',
      'CMAs / Applications',
      'Impugned Order / Record',
      'Authority Record',
      'Comments / Replies',
      'Orders & Judgments',
      'Miscellaneous',
    ],
    defaultStages: [
      'Impugned Decision / Record Review',
      'Maintainability / Alternate Remedy Review',
      'Petition Drafting',
      'Filing',
      'Office Objections / Scrutiny',
      'Preliminary Hearing / Admission',
      'Notice to Authority / Respondents',
      'Record / Comments Called',
      'Rejoinder, if any',
      'Interim Relief / Suspension, if any',
      'Arguments',
      'Judgment / Remand / Directions',
      'Compliance / Further Remedy',
      'Closed',
    ],
    aiContextHints:
      'AI should understand impugned decision, authority, statutory power, jurisdictional error, natural justice, mala fide, procedural defect, alternate remedy, and requested relief.',
  },
  {
    id: 'Constitutional Miscellaneous Application',
    label: 'Constitutional Miscellaneous Application',
    shortLabel: 'CMA',
    description:
      'Connected applications in writ or constitutional proceedings, including stay, suspension, early hearing, impleadment, amendment, restoration, exemption, or production of record.',
    legalBasis: [
      'Stay / Suspension: connected to pending writ/constitutional petition and impugned order/action.',
      'Early / Urgent Hearing: court practice and urgency.',
    ],
    sideOptions: ['Applicant', 'Respondent', 'Petitioner', 'Public Authority'],
    documentTabs: [
      'All',
      'CMA / Application',
      'Main Petition / Record',
      'Supporting Documents',
      'Replies / Objections',
      'Orders',
      'Miscellaneous',
    ],
    defaultStages: [
      'Application Preparation',
      'Filing',
      'Office Objections / Scrutiny',
      'Notice to Respondent, if required',
      'Reply / Objections, if any',
      'Urgent / Interim Hearing, if applicable',
      'Arguments',
      'Order',
      'Compliance / Main Case Linkage',
      'Closed',
    ],
    aiContextHints:
      'AI should understand main writ/constitutional case, application subtype, impugned order, urgency, interim relief, necessary parties, record required, and connection with pending proceedings.',
  },
];

const WRIT_LEGACY_BY_ID = new Map(WRIT_LEGACY_MATTER_TYPES.map((t) => [t.id, t]));

export function getWritLegacyMatterType(matterTypeId: string): CaseMatterType | undefined {
  return WRIT_LEGACY_BY_ID.get(matterTypeId);
}

export function isWritLegacyMatterType(matterTypeId: string): boolean {
  return WRIT_LEGACY_BY_ID.has(matterTypeId);
}

export const WRIT_CASE_TAXONOMY_CONFIG = {
  id: 'constitutional_writ',
  label: 'Constitutional / Writ',
  shortLabel: 'Writ',
  description:
    'Writ petitions, intra-court appeals, contempt proceedings, and other constitutional matters before the High Court.',
  active: true,
  types: [
    {
      id: 'Writ Petition',
      label: 'Writ Petition',
      shortLabel: 'Writ',
      description:
        'Constitutional / writ proceedings under Article 199 of the Constitution of Pakistan.',
      legalBasis: [
        'Article 199 of the Constitution of Pakistan: High Court writ jurisdiction.',
        'Maintainability generally depends on alternate remedy, public law element, locus standi, and jurisdiction.',
      ],
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: [
        'All',
        'Writ Petition',
        'CMAs / Applications',
        'Annexures',
        'Impugned Order / Record',
        'Comments / Replies',
        'Orders & Judgments',
        'Miscellaneous',
      ],
      defaultStages: [
        'Case Preparation',
        'Filing',
        'Office Objections / Scrutiny',
        'Preliminary Hearing / Admission',
        'Notice to Respondents',
        'Comments / Reply',
        'Rejoinder (if any)',
        'Interim Relief / Stay (if any)',
        'Record Called (if required)',
        'Arguments',
        'Judgment / Final Order',
        'Compliance / Further Remedy',
        'Closed',
      ],
      aiContextHints:
        'AI should understand impugned order/action, public authority, statutory duty, alternate remedy, cause of action, fundamental/legal rights, grounds of illegality, mala fide, natural justice, jurisdictional defect, service facts, detention facts if any, and relief sought.',
    },
    {
      id: 'Intra-Court Appeal',
      label: 'Intra-Court Appeal',
      shortLabel: 'ICA',
      description:
        'Appeal before the Division Bench against judgment/order of a Single Bench.',
      legalBasis: [
        'Applicable High Court Rules.',
        'Applicable Intra-Court Appeal law.',
        'Maintainability depends on nature of impugned order, limitation, and High Court rules.',
      ],
      sideOptions: ['Appellant', 'Respondent'],
      documentTabs: [
        'All',
        'Memo of ICA',
        'CMAs / Applications',
        'Impugned Judgment / Order',
        'Record',
        'Orders & Judgments',
        'Miscellaneous',
      ],
      defaultStages: [
        'Case Preparation',
        'Filing',
        'Office Objections / Scrutiny',
        'Preliminary Hearing / Maintainability',
        'Notice to Respondent',
        'Record / Paper Book (if required)',
        'Stay / Suspension (if any)',
        'Arguments',
        'Judgment / Final Order',
        'Closed',
      ],
      aiContextHints:
        'AI should understand single bench order, maintainability, grounds of ICA, limitation, stay, impugned writ/service/public law matter, and prayer.',
    },
    {
      id: 'Contempt Petition',
      label: 'Contempt Petition',
      shortLabel: 'Contempt',
      description:
        'Proceedings for non-compliance/disobedience of court order.',
      legalBasis: [
        'Article 204 of the Constitution of Pakistan.',
        'Contempt of Court Ordinance, 2003.',
        'Maintainability depends on existence of clear order/direction, knowledge, ability to comply, and wilful disobedience.',
      ],
      sideOptions: ['Petitioner / Applicant', 'Respondent / Alleged Contemnor'],
      documentTabs: [
        'All',
        'Contempt Petition',
        'Court Order / Judgment',
        'Compliance Record',
        'CMAs / Applications',
        'Replies',
        'Orders',
        'Miscellaneous',
      ],
      defaultStages: [
        'Case Preparation',
        'Filing',
        'Office Objections / Scrutiny',
        'Preliminary Hearing',
        'Notice to Respondent',
        'Reply / Compliance Report',
        'Rejoinder (if any)',
        'Arguments / Compliance Hearing',
        'Final Order',
        'Disposal / Further Proceedings',
        'Closed',
      ],
      aiContextHints:
        'AI should understand original order, date, operative directions, alleged contemnor, knowledge of order, non-compliance, correspondence, ability to comply, wilfulness, and relief sought.',
    },
    {
      id: 'Other Constitutional Matter',
      label: 'Other Constitutional Matter',
      shortLabel: 'Other',
      description:
        'Use only where the matter does not fit Writ Petition, ICA, or Contempt.',
      legalBasis: [],
      sideOptions: ['Petitioner', 'Respondent', 'Applicant', 'Public Authority'],
      documentTabs: [
        'All',
        'Documents',
        'CMAs / Applications',
        'Record',
        'Orders',
        'Miscellaneous',
      ],
      defaultStages: [
        'Case Preparation',
        'Filing',
        'Office Objections / Scrutiny',
        'Preliminary Hearing',
        'Notice / Reply / Comments',
        'Interim Relief (if any)',
        'Record Called (if any)',
        'Arguments / Hearing',
        'Order / Judgment',
        'Compliance / Further Remedy',
        'Closed',
      ],
      aiContextHints:
        'AI should rely on user-provided description, forum, public authority, legal/fundamental right involved, impugned order/action, uploaded documents, and selected relief.',
    },
  ],
};

/** Matter types that trigger writ-disposed → ICA transition (active + legacy). */
export const WRIT_PETITION_FAMILY_MATTER_TYPES: string[] = [
  'Writ Petition',
  ...WRIT_LEGACY_MATTER_TYPE_IDS.filter((id) => id !== 'Constitutional Miscellaneous Application'),
];

export const normalizeWritCaseClassification = (existingCase: Record<string, unknown>) => {
  if (existingCase.caseCategory === 'Constitutional / Writ') {
    return existingCase;
  }

  const legacyType = String(
    existingCase.matterType || existingCase.caseTypeName || existingCase.caseType || '',
  );
  const legacyTypeLower = legacyType.toLowerCase();

  const caseTypeMap: Record<string, { type: string }> = {
    'writ petition': { type: 'Writ Petition' },
    'petition under article 199': { type: 'Writ Petition' },
    'constitutional petition': { type: 'Writ Petition' },
    'article 184(3) petition': { type: 'Writ Petition' },
    mandamus: { type: 'Writ Petition' },
    certiorari: { type: 'Writ Petition' },
    prohibition: { type: 'Writ Petition' },
    'quo warranto': { type: 'Writ Petition' },
    'service writ': { type: 'Writ Petition' },
    'service matter': { type: 'Writ Petition' },
    'promotion case': { type: 'Writ Petition' },
    'seniority case': { type: 'Writ Petition' },
    'transfer posting case': { type: 'Writ Petition' },
    'regularization case': { type: 'Writ Petition' },
    'pension case': { type: 'Writ Petition' },
    'departmental inquiry': { type: 'Writ Petition' },
    'termination from service': { type: 'Writ Petition' },
    habeas: { type: 'Writ Petition' },
    'habeas corpus': { type: 'Writ Petition' },
    'recovery of detenue': { type: 'Writ Petition' },
    'illegal detention': { type: 'Writ Petition' },
    'missing person': { type: 'Writ Petition' },
    'fundamental rights petition': { type: 'Writ Petition' },
    'public interest petition': { type: 'Writ Petition' },
    'public law matter': { type: 'Writ Petition' },
    'judicial review': { type: 'Writ Petition' },
    'challenge to administrative order': { type: 'Writ Petition' },
    'challenge to tribunal order': { type: 'Writ Petition' },
    'natural justice violation': { type: 'Writ Petition' },
    'intra-court appeal': { type: 'Intra-Court Appeal' },
    ica: { type: 'Intra-Court Appeal' },
    'contempt petition': { type: 'Contempt Petition' },
    'non-compliance of court order': { type: 'Contempt Petition' },
    'constitutional miscellaneous application': { type: 'Writ Petition' },
    cma: { type: 'Writ Petition' },
    'stay application': { type: 'Writ Petition' },
    'early hearing application': { type: 'Writ Petition' },
    'other constitutional / writ matter': { type: 'Other Constitutional Matter' },
    'other constitutional matter': { type: 'Other Constitutional Matter' },
  };

  const mapped = caseTypeMap[legacyTypeLower];

  if (mapped) {
    return {
      ...existingCase,
      caseCategory: 'Constitutional / Writ',
      caseType: mapped.type,
      matterType: mapped.type,
      legacyCaseType: legacyType,
    };
  }

  if (
    legacyTypeLower.includes('writ') ||
    legacyTypeLower.includes('constitutional') ||
    legacyTypeLower.includes('habeas') ||
    legacyTypeLower.includes('mandamus')
  ) {
    return {
      ...existingCase,
      caseCategory: 'Constitutional / Writ',
      caseType: 'Writ Petition',
      matterType: 'Writ Petition',
      legacyCaseType: legacyType,
    };
  }

  return existingCase;
};
